import { useEffect } from 'react';

/**
 * Listens for document mousedown and calls onClickOutside when the click
 * is outside the ref element (and optional portal identified by excludeSelector).
 * Uses capture phase so it runs before other handlers.
 *
 * @param ref - Ref to the container element
 * @param onClickOutside - Called when clicking outside
 * @param isActive - Whether the listener is active (e.g. cell is editing)
 * @param excludeSelector - Optional CSS selector for portaled content to exclude (e.g. '[data-testid="calendar-popover"]')
 */
export function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  onClickOutside: () => void,
  isActive: boolean,
  excludeSelector?: string
): void {
  useEffect(() => {
    if (!isActive) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const isInsideRef = ref.current?.contains(target);

      let isInsideExcluded = false;
      if (excludeSelector) {
        const excludedEl = document.querySelector(excludeSelector);
        isInsideExcluded = excludedEl?.contains(target) ?? false;
      }

      if (!isInsideRef && !isInsideExcluded) {
        onClickOutside();
      }
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    return () => document.removeEventListener('mousedown', handleMouseDown, true);
  }, [ref, onClickOutside, isActive, excludeSelector]);
}
